import * as angular from 'angular';
import { find } from 'lodash';

import { SelectOption } from '@mode/shared/ui-angularjs';
import { EntitledAction } from '@mode/legacy/data-access-model-js';
import { ModalTheme, ModalType } from '@mode/shared/ui-angularjs';

export enum MetaEntitledAction {
  None = 'none',
}
export const EntitledActionState = {
  ...EntitledAction,
  ...MetaEntitledAction,
};
// workaround for the lack of the ability to extend enums
// @see https://github.com/Microsoft/TypeScript/issues/17592
export type EntitledActionState = (typeof EntitledActionState)[keyof typeof EntitledActionState];

export enum SelectFieldType {
  Icon = 'icon',
  Inline = 'inline',
  Default = 'default',
}

export interface EntitlementSelectOption extends SelectOption {
  value: EntitledActionState;
}

const CREATE_OPTIONS: EntitlementSelectOption[] = [
  {
    label: 'Query',
    labelCaption: 'View content and query this connection',
    value: EntitledActionState.Query,
  },
  {
    label: 'Manage',
    labelCaption: 'Query this connection and edit configuration',
    value: EntitledActionState.Manage,
  },
  {
    label: 'View',
    labelCaption: 'View content built on this connection',
    value: EntitledActionState.View,
  },
];

const UPDATE_OPTIONS: EntitlementSelectOption[] = [
  ...CREATE_OPTIONS,
  {
    label: 'Remove Access…',
    value: EntitledActionState.None,
    confirm: {
      title: 'Remove access to connection',
      content: `
        <span>
          Removing access to a connection can impact their ability to use reports, definitions, and datasources.
        </span>
        <p>
          Are you sure you want to remove access?
        </p>
      `,
      buttonConfirm: 'Remove access',
      modalType: ModalType.Default,
      modalTheme: ModalTheme.DangerLight,
    },
  },
];

export const ConnectionPermissionsAccessDropdownComponent: angular.IComponentOptions = {
  controller: class {
    private fieldType: SelectFieldType;
    public value: string;
    public toggleWithIcon: false;
    public selected: EntitlementSelectOption;
    public selectOptions: EntitlementSelectOption[] = CREATE_OPTIONS;
    public removeEnabled: boolean;
    public onChange: (args: { $option: EntitlementSelectOption }) => void;
    private options: EntitledActionState[];

    $onInit(): void {
      this.selectOptions = this.getOptions(this.selectOptions, this.removeEnabled);
      this.selected = this.selectedOption(this.value) || this.selectedOption(EntitledActionState.Query);
      this.fieldType = this.fieldType || SelectFieldType.Default;
    }

    $onChanges(changed: angular.IOnChangesObject) {
      if (changed.value) {
        this.selected = this.selectedOption(this.value);
      }
    }

    getOptions(options: SelectOption[], removeEnabled: boolean): EntitlementSelectOption[] {
      if (this.options) {
        return this.createOptions(this.options);
      }

      if (removeEnabled) {
        return UPDATE_OPTIONS;
      }

      return CREATE_OPTIONS;
    }

    createOptions(options: EntitledActionState[] = []): EntitlementSelectOption[] {
      const sortingOrder: EntitledActionState[] = [
        EntitledActionState.View,
        EntitledActionState.Query,
        EntitledActionState.Manage,
        EntitledActionState.None,
      ];
      const bySortingOrder = (a: EntitlementSelectOption, b: EntitlementSelectOption) =>
        sortingOrder.indexOf(a.value) - sortingOrder.indexOf(b.value);
      const getUpdateOptionByValue = (actionState: EntitledActionState) =>
        UPDATE_OPTIONS.find((opt) => opt.value === actionState);

      return options.map(getUpdateOptionByValue).sort(bySortingOrder);
    }

    handlePermissionClick($evt, value): void {
      const $option = find(this.selectOptions, (option) => option.value === value);
      this.onChange({ $option });
    }

    selectedOption(selectedValue: string | number): EntitlementSelectOption {
      return this.selectOptions.filter((o) => o.value === selectedValue)[0];
    }
  },
  controllerAs: 'ctrl',
  transclude: true,
  bindings: {
    fieldType: '@?',
    value: '<?',
    removeEnabled: '<?',
    onChange: '&',
    options: '<',
  },
  template: `
    <icon-select-field
      ng-if="ctrl.fieldType === '${SelectFieldType.Icon}'"
      on-change="ctrl.handlePermissionClick($evt, $option.value)"
      options="ctrl.selectOptions"
      selected="ctrl.selected"
      title="Access Type"
    />
    <inline-select-field
      ng-if="ctrl.fieldType === '${SelectFieldType.Inline}'"
      on-change="ctrl.handlePermissionClick($evt, $option.value)"
      options="ctrl.selectOptions"
      selected="ctrl.selected"
      title="Access Type"
    />
    <select-field
      ng-if="ctrl.fieldType === '${SelectFieldType.Default}'"
      on-change="ctrl.handlePermissionClick($evt, $option.value)"
      options="ctrl.selectOptions"
      selected="ctrl.selected"
      title="Access Type"
    />
  `,
};
